import Phaser from 'phaser';

/** Opacité du voile noir hors de la zone active (0 = transparent, 1 = noir). */
export const ZONE_DIM_ALPHA = 0.5;
export const ZONE_DIM_COLOR = 0x000000;
/** Durée du fondu à l'entrée/sortie de zone. */
export const ZONE_DIM_FADE_MS = 250;
/**
 * Au-dessus des avatars (tri en Y ≤ ~740) et des calques alwaysAbove (murs,
 * 800), sous les labels de bureau (900), le ping speaker (910) et les
 * réactions (950) — ces repères doivent rester lisibles hors de la zone.
 */
export const ZONE_DIM_DEPTH = 850;
/** Marge (px monde) autour de la carte : couvre l'écran au dézoom maximal. */
const OVERSCAN_PX = 4096;

export interface LitArea { x: number; y: number; width: number; height: number }

/**
 * Assombrit toute la carte sauf la zone où se trouve le joueur local (bulle,
 * salle de réunion ou bureau — choisie par ZoneScene), comme Gather/WorkAdventure.
 *
 * Voile = un Graphics statique (dessiné une fois à la construction), découpé
 * par un GeometryMask inversé qui ne contient que le rectangle éclairé. Rien
 * n'est recalculé par frame : le masque n'est redessiné que quand le
 * rectangle change, et le voile est masqué (setVisible(false), pas de passe
 * stencil) hors zone.
 *
 * WebGL uniquement : GeometryMask.invertAlpha n'est pas supporté en rendu
 * Canvas (Phaser.AUTO peut y retomber) — sans lui, le voile couvrirait aussi
 * la zone active. En Canvas, aucun effet (carte normale).
 */
export class ZoneDimmer {
  private readonly scene: Phaser.Scene;
  private readonly overlay: Phaser.GameObjects.Graphics | null = null;
  private readonly maskShape: Phaser.GameObjects.Graphics | null = null;
  private lit: LitArea | null = null;
  private fade: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, mapWidth: number, mapHeight: number) {
    this.scene = scene;
    if (scene.sys.renderer.type !== Phaser.WEBGL) return;

    this.overlay = scene.add.graphics()
      .setDepth(ZONE_DIM_DEPTH)
      .setAlpha(0)
      .setVisible(false);
    this.overlay.fillStyle(ZONE_DIM_COLOR, 1);
    this.overlay.fillRect(-OVERSCAN_PX, -OVERSCAN_PX, mapWidth + 2 * OVERSCAN_PX, mapHeight + 2 * OVERSCAN_PX);

    // Hors display list : ne sert que de forme au masque.
    this.maskShape = scene.make.graphics({}, false);
    const mask = this.maskShape.createGeometryMask();
    mask.setInvertAlpha(true);
    this.overlay.setMask(mask);
  }

  /** Rectangle (px monde) à laisser éclairé, null = aucun voile. Sans effet si inchangé. */
  setLitArea(area: LitArea | null): void {
    const prev = this.lit;
    if (prev === area || (prev && area && prev.x === area.x && prev.y === area.y
      && prev.width === area.width && prev.height === area.height)) return;
    this.lit = area ? { ...area } : null;
    this.apply(this.lit);
  }

  destroy(): void {
    this.fade?.stop();
    this.fade = null;
    this.overlay?.clearMask(true);
    this.overlay?.destroy();
    this.maskShape?.destroy();
  }

  private apply(bounds: LitArea | null): void {
    if (!this.overlay || !this.maskShape) return;
    if (!bounds) {
      this.fadeTo(0);
      return;
    }

    // Passage direct d'une zone à une autre (salles côte à côte) : le trou
    // change de place, le voile reste — pas de clignotement.
    this.maskShape.clear();
    this.maskShape.fillStyle(0xffffff, 1);
    this.maskShape.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    this.overlay.setVisible(true);
    this.fadeTo(ZONE_DIM_ALPHA);
  }

  private fadeTo(alpha: number): void {
    const overlay = this.overlay;
    if (!overlay) return;
    this.fade?.stop();
    this.fade = null;
    if (overlay.alpha === alpha) {
      if (alpha === 0) overlay.setVisible(false);
      return;
    }
    this.fade = this.scene.tweens.add({
      targets: overlay,
      alpha,
      duration: ZONE_DIM_FADE_MS,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.fade = null;
        if (alpha === 0) overlay.setVisible(false);
      },
    });
  }
}
